import { create } from "zustand"

import type { FaceMeshCapture } from "@/packages/face-mesh"

interface MediaState {
  /** Captured images keyed by quiz step id, as data URLs. In-memory only. */
  photos: Record<string, string>
  /**
   * Face-detection snapshots keyed by the same step id, paired with the photo —
   * the camera captures landmarks once and the processing screen replays them.
   */
  meshes: Record<string, FaceMeshCapture>
  setPhoto: (stepId: string, dataUrl: string) => void
  setMesh: (stepId: string, mesh: FaceMeshCapture | null) => void
  clearPhoto: (stepId: string) => void
}

/**
 * Holds captured photos (and their face-mesh snapshots) for the current session,
 * deliberately NOT persisted: data URLs are large and would blow the
 * sessionStorage quota, and must never leak into the quiz answers (which are sent
 * to analytics and the profile). The quiz answers only carry a lightweight
 * marker; the bytes and landmarks live here until a later step reads them.
 */
export const useMediaStore = create<MediaState>((set) => ({
  photos: {},
  meshes: {},
  setPhoto: (stepId, dataUrl) => set((s) => ({ photos: { ...s.photos, [stepId]: dataUrl } })),
  setMesh: (stepId, mesh) =>
    set((s) => {
      const next = { ...s.meshes }
      if (mesh) next[stepId] = mesh
      else delete next[stepId]
      return { meshes: next }
    }),
  clearPhoto: (stepId) =>
    set((s) => {
      const photos = { ...s.photos }
      const meshes = { ...s.meshes }
      delete photos[stepId]
      delete meshes[stepId]
      return { photos, meshes }
    }),
}))
